'use client';

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_APP_SETTINGS } from '@/lib/appSettings';
import { getDb } from '@/lib/db';
import { normalizeCurrency } from '@/lib/money';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import type { AppSettings } from '@/lib/types';

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function withFallback(settings?: AppSettings | null): AppSettings {
  return {
    ...DEFAULT_APP_SETTINGS,
    ...settings,
    id: 'main',
    currency: normalizeCurrency(settings?.currency),
  };
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const saved = await db.settings.get('main');
      setSettings(withFallback(saved));
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les réglages.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const updateSettings = useCallback(async (payload: Partial<Pick<AppSettings, 'currency'>>) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      const now = new Date().toISOString();
      const current = withFallback(await db.settings.get('main'));
      const next: AppSettings = {
        ...current,
        ...payload,
        id: 'main',
        currency: normalizeCurrency(payload.currency ?? current.currency),
        createdAt: current.createdAt || now,
        updatedAt: now,
      };
      await db.settings.put(next);
      setSettings(next);
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de modifier les réglages.', err));
    }
  }, []);

  const resetSettings = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.settings.clear();
      setSettings(DEFAULT_APP_SETTINGS);
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de réinitialiser les réglages.', err));
    }
  }, []);

  return {
    settings,
    loading,
    error,
    updateSettings,
    resetSettings,
  };
}
